import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
  OnDestroy,
  AfterViewInit,
} from '@angular/core';
import {
  Chart,
  ChartData,
  ChartOptions,
  ChartType,
  registerables,
} from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';

const THEME_KEY = 'app-theme';

@Component({
  selector: 'app-ncs-chart',
  templateUrl: './ncs-chart.component.html',
  styleUrls: ['./ncs-chart.component.scss'],
})
export class NcsChartComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() chartType: ChartType = 'line';
  @Input() data!: ChartData;
  @Input() options!: ChartOptions;
  @Input() oneRecord: any = 0;

  @ViewChild('chartCanvas', { static: true })
  chartCanvas!: ElementRef<HTMLCanvasElement>;

  private chart!: Chart;
  private themeObserver: MutationObserver | null = null;

  constructor() {
    Chart.register(...registerables, zoomPlugin);
  }

  // ---------------- THEME ----------------
  private getTheme() {
    // Check both the document class and localStorage for the most reliable dark mode detection
    const isDark =
      document.documentElement.classList.contains('dark-theme') ||
      localStorage.getItem(THEME_KEY) === 'dark';

    return {
      text: isDark ? '#e4e5e6' : '#444444', // Pure white for better visibility on dark backgrounds
      grid: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.05)',
      tooltipBg: isDark ? 'rgba(15, 15, 15, 0.95)' : 'rgba(255, 255, 255, 0.9)',
    };
  }

  // ---------------- GRADIENTS ----------------
  private getVibrantGradients(ctx: CanvasRenderingContext2D) {
    const make = (c1: string, c2: string) => {
      const g = ctx.createLinearGradient(0, 0, 0, 400);
      g.addColorStop(0, c1);
      g.addColorStop(1, c2);
      return g;
    };

    return [
      make('#7367f0', '#ce9ffc'),
      make('#ea5455', '#feb1bd'),
      make('#28c76f', '#81fbb8'),
      make('#ff9f43', '#fec163'),
      make('#00cfe8', '#59f0ff'),
    ];
  }

  // ---------------- LIFECYCLE ----------------
  ngAfterViewInit(): void {
    this.setupThemeObserver();
    this.initializeChart();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes['data'] && !changes['data'].firstChange) ||
      (changes['chartType'] && !changes['chartType'].firstChange)
    ) {
      this.updateChartData();
      }
    }

  ngOnDestroy(): void {
    if (this.themeObserver) this.themeObserver.disconnect();
    if (this.chart) this.chart.destroy();
      }

  // ---------------- OBSERVER ----------------
  private setupThemeObserver(): void {
    this.themeObserver = new MutationObserver(() => {
      this.updateChartTheme();
    });

    this.themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });
    }

  // ---------------- DATA UPDATE ----------------
  private updateChartData(): void {
    if (this.chart) {
      this.chart.destroy();
    }
    this.initializeChart();
  }

  // ---------------- THEME UPDATE ----------------
  private updateChartTheme(): void {
    if (!this.chart) return;

    const theme = this.getTheme();
    const isRadial = ['pie', 'doughnut'].includes(this.chartType);

    // Update Scales (only if not a Pie chart)
    if (!isRadial && this.chart.options.scales) {
      const scales = this.chart.options.scales as any;
      if (scales.x) {
        scales.x.ticks.color = theme.text;
        scales.x.grid.color = theme.grid;
      }
      if (scales.y) {
        scales.y.ticks.color = theme.text;
        scales.y.grid.color = theme.grid;
      }
    }

    // Update Legend
    if (this.chart.options.plugins?.legend?.labels) {
      this.chart.options.plugins.legend.labels.color = theme.text;
  }

    // Update Tooltip
    if (this.chart.options.plugins?.tooltip) {
      const tooltip = this.chart.options.plugins.tooltip;
      tooltip.backgroundColor = theme.tooltipBg;
      tooltip.titleColor = theme.text;
      tooltip.bodyColor = theme.text;
    }

    this.chart.update('none'); // Update without animation for a seamless theme switch
  }

  // ---------------- INITIALIZE ----------------
  private initializeChart(): void {
    const canvas = this.chartCanvas.nativeElement;
    const ctx = canvas.getContext('2d');

    if (!ctx || !this.data?.datasets?.length) return;

    const theme = this.getTheme();
    const gradients = this.getVibrantGradients(ctx);
    const isRadial = ['pie', 'doughnut'].includes(this.chartType);

    const enhancedData: ChartData = {
      ...this.data,
      datasets: this.data.datasets.map((dataset: any, i: number) => {
        const color = gradients[i % gradients.length];
        return {
          ...dataset,
          backgroundColor: isRadial ? gradients : color,
          // Use vibrant colors for lines to ensure visibility in both modes
          borderColor: isRadial || this.chartType === 'bar' ? '#e4e5e6' : color,
          borderWidth: isRadial ? 2 : 3,
          pointBackgroundColor: color,
          pointBorderColor: '#e4e5e6',
          pointRadius: 4,
          pointHoverRadius: 6,
          tension: 0.4,
          fill: this.chartType !== 'line',
        };
      }),
      };

    this.chart = new Chart(ctx, {
      type: this.chartType,
      data: enhancedData,
      options: {
        responsive: true,
        maintainAspectRatio: false, // Allows the chart to fill the container height
        layout: {
          padding: {
            top: 10,
            bottom: 10,
            left: 10,
            right: 10,
          },
        },
        plugins: {
          legend: {
            display: true,
            position: 'top',
            align: 'center',
            fullSize: true,
            labels: {
              padding: 20, // Space between legend and graph
              font: {
                size: 12,
            },
          },
          },
        },
        // For Pie/Doughnut, ensure it doesn't try to occupy the whole height
        // if there are many legends.
        aspectRatio: isRadial ? 1 : 2,
      },
    });
  }
}
